import { useQuery } from '@tanstack/react-query';
import { Button, Skeleton } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import { listMemberships } from '../../lib/api';

/** A single matrix cell: directs-required (N) for this tier + the reward paid
 *  each time the sponsor's count of that direct tier hits a multiple of N. */
export type GiveMeCell = { directs: number; reward: number };

export type GiveMeSetting = {
  label?: string;
  /** When true, directs of every membership pool into one count per sponsor
   *  tier; the goal is read from the reserved `matrix[sponsor][ANY_DIRECT]`
   *  cell instead of the per-direct-tier grid. */
  unified?: boolean;
  /** Convenience default N pre-filled into newly-entered cells. Not read at
   *  payout time — only the per-cell `directs` matters. */
  default_directs?: number;
  /** matrix[sponsorTierCode][directTierCode] = { directs, reward }. */
  matrix?: Record<string, Record<string, GiveMeCell>>;
};

/** Reserved direct-tier key meaning "any membership" (unified mode). Must match
 *  the backend sentinel in `packages/compplans/src/plans/give-me.ts`. */
const ANY_DIRECT = '*';

export type GiveMeConfig = { settings: GiveMeSetting[] };

export function emptyGiveMeConfig(): GiveMeConfig {
  return { settings: [] };
}

export function GiveMeEditor({
  value,
  onChange,
  readOnly,
}: {
  value: GiveMeConfig;
  onChange: (next: GiveMeConfig) => void;
  readOnly?: boolean;
}) {
  const settings = value.settings ?? [];

  function updateSetting(i: number, next: GiveMeSetting) {
    const list = [...settings];
    list[i] = next;
    onChange({ ...value, settings: list });
  }
  function removeSetting(i: number) {
    onChange({ ...value, settings: settings.filter((_, idx) => idx !== i) });
  }
  function addSetting() {
    onChange({
      ...value,
      settings: [
        ...settings,
        { label: `Give Me ${settings.length + 1}`, default_directs: 4, matrix: {} },
      ],
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground max-w-2xl">
          Each GM Setting is a sponsor-tier (row) × direct-tier (column) grid. A
          cell sets how many directs of that tier are needed (N) and the reward
          paid each time the sponsor's lifetime count of that tier hits a
          multiple of N (e.g. 4 → reward, 8 → reward again). Blank cells don't
          count. Each tier is counted separately; only PAID directs count.
          Toggle <b>Unified count</b> on a setting to pool every membership into
          one count per sponsor tier (a single "Any direct" goal).
        </p>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={addSetting}>
            <Plus className="h-3 w-3 mr-1" />
            Add GM Setting
          </Button>
        ) : null}
      </div>

      {settings.length === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-4">
          No GM Settings yet. Click "Add GM Setting" to create one.
        </p>
      ) : (
        settings.map((setting, i) => (
          <GiveMeSettingCard
            key={i}
            index={i}
            setting={setting}
            onChange={(next) => updateSetting(i, next)}
            onRemove={() => removeSetting(i)}
            readOnly={readOnly}
          />
        ))
      )}
    </div>
  );
}

function GiveMeSettingCard({
  index,
  setting,
  onChange,
  onRemove,
  readOnly,
}: {
  index: number;
  setting: GiveMeSetting;
  onChange: (next: GiveMeSetting) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const tiersQ = useQuery({ queryKey: ['admin-memberships'], queryFn: listMemberships });
  const matrix = setting.matrix ?? {};
  const defaultN = setting.default_directs ?? 0;

  function getCell(sponsor: string, direct: string): GiveMeCell | undefined {
    return matrix[sponsor]?.[direct];
  }
  function setCellField(
    sponsor: string,
    direct: string,
    field: 'directs' | 'reward',
    raw: string,
  ) {
    const nextMatrix: Record<string, Record<string, GiveMeCell>> = {};
    for (const [s, row] of Object.entries(matrix)) {
      nextMatrix[s] = {};
      for (const [d, cell] of Object.entries(row)) nextMatrix[s]![d] = { ...cell };
    }
    const row = nextMatrix[sponsor] ?? {};
    const existing = row[direct] ?? { directs: defaultN, reward: 0 };
    const num = raw.trim() === '' ? null : Number(raw) || 0;
    const updated: GiveMeCell = { ...existing, [field]: num ?? 0 };
    // Drop the cell entirely when both fields are cleared/zero.
    const cleared =
      (field === 'reward' && num === null && (existing.directs ?? 0) <= 0) ||
      (field === 'directs' && num === null && (existing.reward ?? 0) <= 0);
    if (cleared || (updated.directs <= 0 && updated.reward <= 0)) {
      delete row[direct];
    } else {
      row[direct] = updated;
    }
    if (Object.keys(row).length === 0) delete nextMatrix[sponsor];
    else nextMatrix[sponsor] = row;
    onChange({ ...setting, matrix: nextMatrix });
  }

  const tiers = tiersQ.data ?? [];
  const unified = setting.unified === true;
  // In unified mode the direct dimension collapses to a single "Any" column
  // backed by the reserved ANY_DIRECT key.
  const directCols = unified
    ? [{ id: ANY_DIRECT, code: 'Any direct', name: 'Any membership' }]
    : tiers;

  return (
    <div className="border rounded-md p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <input
            type="text"
            className="h-8 w-48 px-2 rounded-md border bg-background text-sm font-medium disabled:opacity-50"
            value={setting.label ?? ''}
            placeholder={`GM Setting ${index + 1}`}
            disabled={readOnly}
            onChange={(e) => onChange({ ...setting, label: e.target.value })}
          />
          <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            Default N
            <input
              type="number"
              className="h-8 w-16 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
              value={setting.default_directs ?? ''}
              min={1}
              step={1}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  ...setting,
                  default_directs: e.target.value === '' ? undefined : Number(e.target.value) || 0,
                })
              }
            />
          </label>
          <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={setting.unified === true}
              disabled={readOnly}
              onChange={(e) => onChange({ ...setting, unified: e.target.checked })}
            />
            Unified count (any membership)
          </label>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive transition-colors"
            aria-label={`Remove ${setting.label ?? `GM Setting ${index + 1}`}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {tiersQ.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : tiers.length === 0 ? (
        <p className="text-xs text-muted-foreground border rounded-md p-4">
          No membership tiers yet. Create them under Members → Memberships.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">
                  {unified ? 'Sponsor \\ Any' : 'Sponsor \\ Direct'}
                </th>
                {directCols.map((t) => (
                  <th key={t.id} className="px-3 py-2 font-mono font-medium" title={t.name}>
                    {t.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tiers.map((sponsor) => (
                <tr key={sponsor.id} className="border-t align-top">
                  <td
                    className="px-3 py-2 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                    title={sponsor.name}
                  >
                    {sponsor.code}
                  </td>
                  {directCols.map((direct) => {
                    // Real tiers key the matrix by membership code; the unified
                    // "Any" column keys by the reserved ANY_DIRECT id.
                    const directKey = unified ? direct.id : direct.code;
                    const cell = getCell(sponsor.code, directKey);
                    return (
                      <td key={direct.id} className="px-2 py-1.5">
                        <div className="flex flex-col gap-1">
                          <label className="flex items-center gap-1 text-[9px] text-muted-foreground">
                            <span className="w-3">N</span>
                            <input
                              type="number"
                              className="h-7 w-16 px-1.5 rounded-md border bg-background text-xs disabled:opacity-50"
                              placeholder={defaultN ? String(defaultN) : '0'}
                              value={cell?.directs ?? ''}
                              min={1}
                              step={1}
                              disabled={readOnly}
                              onChange={(e) =>
                                setCellField(sponsor.code, directKey, 'directs', e.target.value)
                              }
                            />
                          </label>
                          <label className="flex items-center gap-1 text-[9px] text-muted-foreground">
                            <span className="w-3">₱</span>
                            <input
                              type="number"
                              className="h-7 w-16 px-1.5 rounded-md border bg-background text-xs disabled:opacity-50"
                              placeholder="0"
                              value={cell?.reward ?? ''}
                              min={0}
                              step={0.01}
                              disabled={readOnly}
                              onChange={(e) =>
                                setCellField(sponsor.code, directKey, 'reward', e.target.value)
                              }
                            />
                          </label>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
